// explore.4xciv.com's boot: the cruise sim and the Atlas, with no game on
// top - no captains, crafting, combat or mission beats. See docs/README.md
// and tools/preview/explore-bundle-check.mjs, which fails the build if any
// of that creeps back in.

import * as THREE from 'three';
import { createPageRenderer } from './gl/pageRenderer';
import { createCruiseView, MAX_REAL_DT_S } from './cruise/createCruiseView';
import { EXPLORE_GROUND } from './atlas/groundGame.explore';
import { composeExploreSystems, EXPLORE_SYSTEMS_ORDER } from './exploreSystems';
import { assertSystemsOrder } from './playSystems';
import { mirrorModeClasses } from './appMode';
import { initMenu, isMenuOpen, showMenu } from './menu';
import { EXPLORE_TABS } from './menuTabs.explore';
import { setAnalytics } from './visits/bus';
import { createAnalytics } from './visits/createVisits';
import { createOverwatchRelay } from './visits/relay';
import { profiler } from './perf';
import { runSystems, type FrameSystem, type FrameCtx } from './frame';
import { gameClock } from './world/clock';
import { viewWarpFrozen, warpStaleRate } from './sim/warp';
import { createMapHold } from './cruise/mapHold';
import { TEST_SWITCHES } from './urlFlags.ts';

const app = document.getElementById('app')!;

// tick() is declared inside the guard below, so the restore handler
// createPageRenderer wires up here reaches it through this indirection
// rather than needing it in scope yet.
let resumeTick: () => void = () => {};

const page = createPageRenderer(app, () => resumeTick());

// A failed context has already painted its notice into #app; nothing below
// this line can run without one.
if (page) {

resumeTick = tick;
const { renderer, contextLoss } = page;

const clock = new THREE.Clock();

// The cruise view (src/cruise/createCruiseView.ts): flight, the ship, the
// camera rig, the pilot HUD, the delta-v map, warp and the land verb, with
// every seam at its default - explore hands the game none of its callbacks,
// so `inFight()` reads false and nothing here ever sees a fight.
const cruise = createCruiseView({
  renderer, contextLoss, app, clock, ground: EXPLORE_GROUND,
});
const {
  scene, flight, gravityField, shipWorldAt,
  _ground, _pulls, _pullIds, _pullRows,
  landVerb, warp, cruiseDrive,
} = cruise;

window.addEventListener('resize', () => {
  renderer.setSize(window.innerWidth, window.innerHeight);
});

mirrorModeClasses();
initMenu(EXPLORE_TABS);
showMenu();

setAnalytics(createAnalytics({
  site: 'explore',
  measurementId: import.meta.env.VITE_GA_MEASUREMENT_ID,
  build: import.meta.env.VITE_BUILD_ID,
  relay: createOverwatchRelay(),
  renderer, scene,
}));

// `?atlashold=1` / `?mapholdoff=1`: the same DEV-only controls play's own
// frameCtx() carries, so a headless check can force the map-hold reading
// open on explore too.
const ATLAS_HOLD = TEST_SWITCHES && new URLSearchParams(location.search).get('atlashold') === '1';
const MAP_HOLD_OFF = TEST_SWITCHES && new URLSearchParams(location.search).get('mapholdoff') === '1';

// No fight here to hold the clock for instead - explore always reads false.
const mapHoldCtl = createMapHold({
  landVerb, flight, gravityField, cruiseDrive, _pulls, _ground, _pullRows, _pullIds, shipWorldAt,
});

function frameCtx(): FrameCtx {
  // Read before the branch below, not inside it: a wheel out past the ORBIT
  // edge hands the camera back, and the tick it happens on then draws the
  // sim rather than skipping a frame nobody drew.
  landVerb.pollHandover();
  if (isMenuOpen() || landVerb.open) {
    if (!viewWarpFrozen()) warp.update();
    const menuRealDt = Math.min(clock.getDelta(), 0.1);
    mapHoldCtl.applyMapHold(ATLAS_HOLD || MAP_HOLD_OFF ? null : mapHoldCtl.readMapHold(menuRealDt, false));
    gameClock().update(ATLAS_HOLD ? 0 : menuRealDt);
    const menuDt = ATLAS_HOLD || mapHoldCtl.current !== null ? 0 : menuRealDt * gameClock().rate;
    return {
      realDt: menuRealDt, dt: menuDt, t: clock.elapsedTime,
      editorOpen: false, paused: true, landOpen: landVerb.open, live: false,
    };
  }

  mapHoldCtl.applyMapHold(null);
  const realDt = Math.min(clock.getDelta(), MAX_REAL_DT_S);
  const preWarpRate = gameClock().rate;
  warp.update();
  const autopilot = cruiseDrive.autopilot;
  const solveFrozen = autopilot !== null && autopilot.freezes(gameClock().jdTdb());
  gameClock().update(solveFrozen ? 0 : realDt);
  const dt = solveFrozen ? 0 : realDt * (warpStaleRate() ? preWarpRate : gameClock().rate);
  return {
    realDt, dt, t: clock.elapsedTime,
    editorOpen: false, paused: false, landOpen: landVerb.open, live: true,
  };
}

const SYSTEMS: FrameSystem[] = composeExploreSystems({
  menu: cruise.systems.menu, live: cruise.systems.live, view: cruise.systems.view,
});

if (import.meta.env.DEV) {
  assertSystemsOrder(SYSTEMS, EXPLORE_SYSTEMS_ORDER);
}

function tick(): void {
  if (contextLoss.lost) return; // no rAF re-armed below; the restore handler kicks it back off

  profiler.mark('frame');
  const ctx = frameCtx();
  runSystems(SYSTEMS, ctx, profiler);
  profiler.end('frame');
  profiler.endFrame();
  // No DEV cluster ships with explore yet, so this paints nothing today; kept
  // so a future perf readout needs no second call site.
  if (ctx.live) cruise.perfPanel.update();

  requestAnimationFrame(tick);
}

cruise.boot();

tick();

}
